import React, {useState} from 'react';
import {Button, ListItemIcon, ListItemText, Menu, MenuItem, Typography} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {DeleteOutline, EditOutlined, PlayCircleOutline, ReplayOutlined, StarOutline} from "@mui/icons-material";
import {RoleCheck} from "../../library/RoleCheck";
import ConfirmDeleteModal from "../modal/ConfirmDeleteModal";
import ModalBase from "../modal/ModalBase";
import InfoAlert from "../alert/InfoAlert";
import AsyncButton from "../button/AsyncButton";
import {useProjectData} from "../../hooks/useProjectData";
import {useToasts} from "../../hooks/useToasts";
import ApiError from "../../api/ApiError"
import {ModelPlatform, resolveNameForModelPlatform} from "../../api/enum/ModelPlatform"
import {EndpointStatus} from "../../api/enum/EndpointStatus"
import RenameModal from "../modal/RenameModal"
import {CustomModelRequests} from "../../api/requests/CustomModelRequests"
import {QualityCheckRequests} from "../../api/requests/QualityCheckRequests"
import {TestRunRequests} from "../../api/requests/TestRunRequests"
import {ProjectRequests} from "../../api/requests/ProjectRequests"
import {AnyCustomModelJson} from "../../api/json/CustomModelJson"
import {ProjectJson} from "../../api/json/ProjectJson"
import PlaygroundModal from "../projects/Playground/PlaygroundModal"

interface CustomModelActionsMenuProps {
    model: AnyCustomModelJson
    project: ProjectJson
    onModelUpdated: (model: AnyCustomModelJson) => void
    onProjectUpdated: (project: ProjectJson) => void
    didRename: (name: string) => void
    didDelete: () => void
}

function CustomModelActionsMenu({model, project, onModelUpdated, onProjectUpdated, didRename, didDelete}: CustomModelActionsMenuProps) {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement>()
    const [endpointStatus, setEndpointStatus] = useState<EndpointStatus>()

    const [playgroundOpen, setPlaygroundOpen] = useState(false)

    const [defaultModalOpen, setDefaultModalOpen] = useState(false)
    const [settingDefault, setSettingDefault] = useState(false)
    const [defaultError, setDefaultError] = useState<ApiError>()

    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [deleteError, setDeleteError] = useState<ApiError>()

    const [renameModalOpen, setRenameModalOpen] = useState(false)
    const [renameInProgress, setRenameInProgress] = useState(false)
    const [renameError, setRenameError] = useState<ApiError>()

    const {id} = useProjectData()
    const toasts = useToasts()

    const isDefault = model.id === project.defaultCustomModelId
    const canMakeDefault = model.completed && !isDefault
    const canRerunQualityCheck = !!model.qualityCheck && model.qualityCheck.stats.completedTestRuns > 0

    // Together fine-tunes only serve while their dedicated endpoint is online, so the playground
    // can't reach an offline one. OpenAI models are serverless and always reachable.
    const requiresOnlineEndpoint = model.platform === ModelPlatform.TOGETHER
    const tryItDisabled = requiresOnlineEndpoint && endpointStatus !== EndpointStatus.STARTED

    const openMenu = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget)
        if (model.completed && requiresOnlineEndpoint) {
            CustomModelRequests.endpoints(model.id)
                .then(endpoints => setEndpointStatus(endpoints.find(it => it.status !== EndpointStatus.DELETED)?.status))
                .catch(() => {})
        }
    }

    const handleRerunQualityCheck = () => {
        const qualityCheckId = model.qualityCheck?.id
        if (!qualityCheckId) return

        TestRunRequests.deleteAll(qualityCheckId)
            .then(() => QualityCheckRequests.restart(qualityCheckId))
            .then(qualityCheck => {
                toasts.showMessage("Quality check restarted.", "success")
                onModelUpdated({...model, qualityCheck} as AnyCustomModelJson)
            })
            .catch(toasts.showError)
    }

    const closeMenu = () => setMenuAnchor(undefined)

    const openFromMenu = (open: () => void) => () => {
        closeMenu()
        open()
    }

    const confirmMakeDefault = () => {
        setSettingDefault(true)
        ProjectRequests.setDefaultCustomModel(project.id, model.id)
            .then(updated => {
                onProjectUpdated(updated)
                setDefaultModalOpen(false)
            })
            .catch(setDefaultError)
            .finally(() => setSettingDefault(false))
    }

    const handleDelete = (deleteFromPlatform: boolean) => {
        CustomModelRequests.delete(id, model.id, deleteFromPlatform)
            .then(() => {
                setDeleteModalOpen(false)
                didDelete()
            })
            .catch(setDeleteError)
    }

    const handleRename = (newName: string) => {
        setRenameInProgress(true)
        // @ts-ignore - deprecated: will be removed with TemplatedModel cleanup
        CustomModelRequests.update(model.id, newName)
            .then(() => {
                setRenameError(undefined)
                setRenameModalOpen(false)
                didRename(newName)
            })
            .catch(setRenameError)
            .finally(() => setRenameInProgress(false))
    }

    return (
        <>
            <Button variant="outlined"
                    color="secondary"
                    endIcon={<KeyboardArrowDownIcon/>}
                    onClick={openMenu}>
                Actions
            </Button>

            <Menu anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={closeMenu}
                  anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                  transformOrigin={{vertical: "top", horizontal: "right"}}>
                {model.completed &&
                    <MenuItem disabled={tryItDisabled} onClick={openFromMenu(() => setPlaygroundOpen(true))}>
                        <ListItemIcon><PlayCircleOutline/></ListItemIcon>
                        <ListItemText secondary={tryItDisabled ? "Endpoint offline" : undefined}>Try it</ListItemText>
                    </MenuItem>
                }
                {canMakeDefault &&
                    <MenuItem onClick={openFromMenu(() => { setDefaultError(undefined); setDefaultModalOpen(true) })}>
                        <ListItemIcon><StarOutline/></ListItemIcon>
                        <ListItemText>Make project default</ListItemText>
                    </MenuItem>
                }
                {canRerunQualityCheck &&
                    <MenuItem onClick={openFromMenu(handleRerunQualityCheck)}>
                        <ListItemIcon><ReplayOutlined/></ListItemIcon>
                        <ListItemText>Re-run quality check</ListItemText>
                    </MenuItem>
                }
                <MenuItem onClick={openFromMenu(() => setRenameModalOpen(true))}>
                    <ListItemIcon><EditOutlined/></ListItemIcon>
                    <ListItemText>Rename</ListItemText>
                </MenuItem>
                {RoleCheck.isAdmin() &&
                    <MenuItem onClick={openFromMenu(() => setDeleteModalOpen(true))}>
                        <ListItemIcon><DeleteOutline color="error"/></ListItemIcon>
                        <ListItemText><Typography color="error">Delete</Typography></ListItemText>
                    </MenuItem>
                }
            </Menu>

            <PlaygroundModal open={playgroundOpen}
                             onClose={() => setPlaygroundOpen(false)}
                             model={model}/>

            <ModalBase open={defaultModalOpen}
                       setOpen={setDefaultModalOpen}
                       title="Change default model"
                       error={defaultError}
                       buttons={
                           <>
                               <Button variant="outlined" color="secondary" disabled={settingDefault}
                                       onClick={() => setDefaultModalOpen(false)}>Cancel</Button>
                               <AsyncButton waiting={settingDefault} onClick={confirmMakeDefault}>Switch model</AsyncButton>
                           </>
                       }>
                <InfoAlert>
                    This model backs your public API. Switching takes effect immediately for live API calls.
                </InfoAlert>
            </ModalBase>

            <ConfirmDeleteModal
                heading={`Delete "${model.name}" fine-tune`}
                body={`This will permanently remove the fine-tune from Entry Point.`}
                checkboxLabel={`Delete from ${resolveNameForModelPlatform(model.platform)}`}
                open={deleteModalOpen}
                setOpen={setDeleteModalOpen}
                confirmedDelete={(checkboxValue) => handleDelete(checkboxValue === true)}
                error={deleteError}
            />

            <RenameModal heading="Rename Model"
                         currentName={model.name}
                         open={renameModalOpen}
                         setOpen={setRenameModalOpen}
                         handleRename={handleRename}
                         renameInProgress={renameInProgress}
                         error={renameError}
            />
        </>
    )
}

export default CustomModelActionsMenu;
